import axios from 'axios';
import { http } from './http';

/**
 * Échanges avec les élèves (`/v1/student-messaging`), distincts de la
 * messagerie interne de l'équipe (`/v1/messaging`). Les droits et les règles
 * restent côté serveur : ces types décrivent seulement ce que l'écran lit.
 */
const root = '/v1/student-messaging';

/** Pas de temps réel dans cette version : la liste et le fil se rafraîchissent. */
export const STUDENT_MESSAGING_REFRESH_MS = 20_000;
export const MAX_MESSAGE_LENGTH = 4000;
export const MIN_STUDENT_SEARCH_LENGTH = 2;

export type StaffRole =
  | 'owner'
  | 'manager'
  | 'agent'
  | 'read_only'
  | 'instructor';
export type ConversationKind = 'OFFICE' | 'DIRECT' | 'GROUP';

export interface StaffContact {
  id: string;
  name: string;
  role: StaffRole;
}

export interface StudentMessagingContacts {
  role: string;
  canOpenOffice: boolean;
  canOpenDirect: boolean;
  canCreateGroup: boolean;
  staff: StaffContact[];
}

export interface StudentContact {
  id: string;
  name: string;
}

export interface StudentConversation {
  id: string;
  kind: ConversationKind;
  title: string;
  candidateId?: string | null;
  unread: number;
  updatedAt: string;
  last: { body: string; author: string; at: string } | null;
}

export interface StudentMessage {
  id: string;
  sequence: number;
  mine: boolean;
  authorKind: 'staff' | 'student';
  author: string;
  body: string;
  createdAt: string;
}

export interface StudentThread {
  conversation: {
    id: string;
    kind: ConversationKind;
    title: string;
    canWrite: boolean;
  };
  members: { kind: 'staff' | 'student'; role: string; name: string }[];
  /** Du plus ancien au plus récent. */
  items: StudentMessage[];
  hasMore: boolean;
}

export interface CreateStudentGroupPayload {
  requestId: string;
  title: string;
  staffIds: string[];
  candidateIds: string[];
}

export const studentMessagingKeys = {
  conversations: (userId?: string) =>
    ['student-messaging', userId, 'conversations'] as const,
  contacts: (userId?: string) =>
    ['student-messaging', userId, 'contacts'] as const,
  students: (userId: string | undefined, search: string) =>
    ['student-messaging', userId, 'students', search] as const,
  thread: (userId: string | undefined, conversationId: string) =>
    ['student-messaging', userId, 'thread', conversationId] as const,
};

export const studentMessagingApi = {
  async getContacts(): Promise<StudentMessagingContacts> {
    const { data } = await http.get<StudentMessagingContacts>(
      `${root}/contacts`,
    );
    return data;
  },
  async searchStudents(search: string): Promise<StudentContact[]> {
    const { data } = await http.get<StudentContact[]>(`${root}/students`, {
      params: { search },
    });
    return Array.isArray(data) ? data : [];
  },
  async getConversations(): Promise<StudentConversation[]> {
    const { data } = await http.get<StudentConversation[]>(
      `${root}/conversations`,
    );
    // La liste alimente aussi le compteur de la navigation, affiché sur tous
    // les écrans : une réponse inattendue ne doit pas les faire tomber.
    return Array.isArray(data) ? data : [];
  },
  /** Ouvre, ou retrouve, l'échange à deux avec cet élève. */
  async openDirect(candidateId: string): Promise<{ id: string }> {
    const { data } = await http.post<{ id: string }>(
      `${root}/conversations/direct`,
      { candidateId },
    );
    return data;
  },
  async createGroup(payload: CreateStudentGroupPayload): Promise<{ id: string }> {
    const { data } = await http.post<{ id: string }>(
      `${root}/conversations/group`,
      payload,
    );
    return data;
  },
  /** Les 50 derniers messages, ou les 50 qui précèdent la séquence `before`. */
  async getMessages(id: string, before?: number): Promise<StudentThread> {
    const { data } = await http.get<StudentThread>(
      `${root}/conversations/${id}/messages`,
      { params: before ? { before } : undefined },
    );
    return data;
  },
  /** `requestId` reste le même lors d'un nouvel essai : pas de doublon. */
  async sendMessage(
    id: string,
    payload: { requestId: string; body: string },
  ): Promise<{ id: string; sequence: number }> {
    const { data } = await http.post<{ id: string; sequence: number }>(
      `${root}/conversations/${id}/messages`,
      payload,
    );
    return data;
  },
  async markRead(id: string, sequence: number): Promise<{ sequence: number }> {
    const { data } = await http.post<{ sequence: number }>(
      `${root}/conversations/${id}/read`,
      { sequence },
    );
    return data;
  },
};

/** Le message du serveur, tel quel ; `fallback` quand il n'a pas répondu. */
export function studentMessagingError(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data: unknown = error.response?.data;
    const message =
      typeof data === 'object' && data !== null
        ? (data as Record<string, unknown>).message
        : undefined;
    if (typeof message === 'string' && message) return message;
    if (Array.isArray(message) && message.length) return message.join(' ');
  }
  return fallback;
}

/** Refus du serveur (accès retiré, conversation indisponible…), par opposition à une panne passagère. */
export function isRefusal(error: unknown): boolean {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  return status !== undefined && status >= 400 && status < 500 && status !== 429;
}

/**
 * Réunit le fil affiché et la dernière page reçue (les messages les plus
 * récents). Les pages plus anciennes déjà chargées sont conservées ; s'il
 * manque des messages entre les deux, on repart de la page reçue.
 */
export function mergeLatestPage(
  shown: StudentThread | undefined,
  latest: StudentThread,
): StudentThread {
  if (!shown?.items.length || !latest.items.length) return latest;
  const newestShown = shown.items[shown.items.length - 1].sequence;
  const oldestLatest = latest.items[0].sequence;
  if (oldestLatest > newestShown + 1) return latest;
  const older = shown.items.filter((m) => m.sequence < oldestLatest);
  return {
    ...latest,
    items: [...older, ...latest.items],
    hasMore: older.length ? shown.hasMore : latest.hasMore,
  };
}

/** Ajoute en tête du fil affiché une page de messages précédents. */
export function prependOlderPage(
  shown: StudentThread,
  older: StudentThread,
): StudentThread {
  const oldestShown = shown.items[0]?.sequence ?? Number.POSITIVE_INFINITY;
  return {
    ...shown,
    items: [
      ...older.items.filter((m) => m.sequence < oldestShown),
      ...shown.items,
    ],
    hasMore: older.hasMore,
  };
}
